import  { useState, useEffect, useRef } from "react";
import {
  Card,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Button,
  Stack,
  Paper,
  LinearProgress,
  Alert,
} from "@mui/material";
import {
  Description as DocumentIcon,
  Fullscreen as FullscreenIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  ErrorOutline as ErrorIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { read, utils } from "xlsx";
import { useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";

const SUPPORTED_EXTENSIONS = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xlsx', 'xls', 'csv'];

const PREVIEW_METHODS = {
  GOOGLE: 'google',
  MICROSOFT: 'microsoft',
  TABLE: 'table'
};

const FilePreview = ({ file, maxHeight = "500px" }) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [excelData, setExcelData] = useState(null);
  const [currentMethod, setCurrentMethod] = useState(PREVIEW_METHODS.GOOGLE);
  const [failedMethods, setFailedMethods] = useState(new Set());
  const [retryCount, setRetryCount] = useState(0);
  const iframeRef = useRef(null);
  const iframeTimeoutRef = useRef(null);

  const fileName = file?.fileName || 'unnamed-file';
  const fileUrl = file?.fileUrl || '';
  const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
  const fileBaseName = fileName.replace(/\.[^/.]+$/, "");

  const isSpreadsheet = ["xlsx", "xls", "csv"].includes(fileExtension);
  const canPreview = SUPPORTED_EXTENSIONS.includes(fileExtension);

  const createViewerUrl = (url, method) => {
    const encodedUrl = encodeURIComponent(url);
    switch (method) {
      case PREVIEW_METHODS.GOOGLE:
        return `https://docs.google.com/viewer?url=${encodedUrl}&embedded=true`;
      case PREVIEW_METHODS.MICROSOFT:
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
      default:
        return url;
    }
  };

  const getMethodName = (method) => {
    switch (method) {
      case PREVIEW_METHODS.GOOGLE:
        return 'Google Viewer';
      case PREVIEW_METHODS.MICROSOFT:
        return 'Microsoft Viewer';
      case PREVIEW_METHODS.TABLE:
        return 'Table View';
      default:
        return 'Unknown';
    }
  };

  const getNextAvailableMethod = () => {
    const methods = [PREVIEW_METHODS.GOOGLE, PREVIEW_METHODS.MICROSOFT, PREVIEW_METHODS.TABLE];
    return methods.find(method => !failedMethods.has(method)) || null;
  };

  const processSpreadsheet = async () => {
    if (!isSpreadsheet) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error('Failed to fetch file');
      const arrayBuffer = await response.arrayBuffer();
      const workbook = read(arrayBuffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = utils.sheet_to_json(worksheet, { header: 1 });

      if (jsonData.length === 0) {
        setExcelData({ columns: [], rows: [], isEmpty: true });
      } else {
        const columns = jsonData[0].map((col, index) => ({
          field: `col${index}`,
          headerName: String(col || `Column ${index + 1}`),
          width: 150,
          flex: 0, 
        }));

        const rows = jsonData.slice(1).map((row, index) => {
          const rowData = { id: index };
          row.forEach((cell, i) => {
            rowData[`col${i}`] = cell || '';
          });
          return rowData;
        });

        setExcelData({ columns, rows, isEmpty: false });
      }
      setCurrentMethod(PREVIEW_METHODS.TABLE);
    } catch (err) {
      setError('Failed to load spreadsheet');
      handleMethodFailure(PREVIEW_METHODS.TABLE);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMethodFailure = (method) => {
    setFailedMethods(prev => new Set([...prev, method]));
    const nextMethod = getNextAvailableMethod();
    
    if (nextMethod) {
      
      setCurrentMethod(nextMethod);
      setRetryCount(prev => prev + 1);
    } else {
      setError('All preview methods failed. Please download the file to view it.');
    }
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
    if (iframeTimeoutRef.current) {
      clearTimeout(iframeTimeoutRef.current);
    }
  };

  const handleIframeError = () => {
    setIsLoading(false);
    if (iframeTimeoutRef.current) {
      clearTimeout(iframeTimeoutRef.current);
    }
    handleMethodFailure(currentMethod);
  };

  const loadIframePreview = (method) => {
    if (!iframeRef.current) return;
    
    setIsLoading(true);
    setError(null);
    

    if (iframeTimeoutRef.current) {
      clearTimeout(iframeTimeoutRef.current);
    }
    
    iframeTimeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      handleMethodFailure(method);
    }, 10000);

    iframeRef.current.src = createViewerUrl(fileUrl, method);
  };

  const handleRefresh = () => {
    setError(null);
    setFailedMethods(new Set());
    setRetryCount(0);
    setCurrentMethod(PREVIEW_METHODS.GOOGLE);
    setExcelData(null);
    
    if (isSpreadsheet) {
      processSpreadsheet();
    } else if (canPreview) {
      loadIframePreview(PREVIEW_METHODS.GOOGLE);
    }
  };

  const handleFullscreen = () => {
    if (isSpreadsheet || currentMethod === PREVIEW_METHODS.TABLE) {
      window.open(fileUrl, "_blank");
    } else {
      window.open(createViewerUrl(fileUrl, currentMethod), "_blank");
    }
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (!file || !fileUrl) return;
    
    // Reset state
    setIsLoading(true);
    setError(null);
    setExcelData(null);
    setFailedMethods(new Set());
    setRetryCount(0);
    setCurrentMethod(PREVIEW_METHODS.GOOGLE);

    if (isSpreadsheet) {
      processSpreadsheet();
    } else if (canPreview) {
      loadIframePreview(PREVIEW_METHODS.GOOGLE);
    } else {
      setIsLoading(false);
    }

    return () => {
      if (iframeTimeoutRef.current) {
        clearTimeout(iframeTimeoutRef.current);
      }
    };
  }, [file, fileUrl, isSpreadsheet,canPreview]);

  
  useEffect(() => {
    if (retryCount === 0) return; 
    
    if (currentMethod === PREVIEW_METHODS.TABLE && isSpreadsheet) {
      processSpreadsheet();
    } else if (currentMethod !== PREVIEW_METHODS.TABLE && canPreview) {
      loadIframePreview(currentMethod);
    }
  }, [currentMethod, retryCount]);

  if (!file) {
    return (
      <Paper sx={{
        p: 4,
        height: maxHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        textAlign: "center",
        border: "2px dashed #ccc"
      }}>
        <ViewIcon sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
        <Typography variant="h6" color="textSecondary">
          No File Selected
        </Typography>
      </Paper>
    );
  }

  return (
    <Card sx={{ height: maxHeight, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ p: 2, bgcolor: theme.palette.primary.light + "20", borderBottom: "1px solid #e0e0e0" }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{
            p: 1.2,
            borderRadius: 1,
            bgcolor: theme.palette.primary.main + "33",
            display: "flex",
            alignItems: "center"
          }}>
            <DocumentIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}>
              {fileBaseName}
            </Typography>
            {failedMethods.size > 0 && (
              <Typography variant="caption" color="textSecondary">
                Using {getMethodName(currentMethod)}
                {failedMethods.size > 0 && ` (${failedMethods.size} method${failedMethods.size > 1 ? 's' : ''} failed)`}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} size="small"><RefreshIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Open in New Tab">
              <IconButton onClick={handleFullscreen} size="small"><FullscreenIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Download">
              <IconButton onClick={handleDownload} size="small"><DownloadIcon /></IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

     
      {isLoading && <LinearProgress />}

      
      {failedMethods.size > 0 && !error && (
        <Alert 
          severity="warning" 
          icon={<WarningIcon />}
          sx={{ m: 1, mb: 0 }}
        >
          Some preview methods failed. Currently using {getMethodName(currentMethod)}.
        </Alert>
      )}

     
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      
        {error && (
          <Box sx={{
            flex: 1,
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center"
          }}>
            <ErrorIcon sx={{ fontSize: 48, color: "#f44336", mb: 2 }} />
            <Typography variant="h6" color="error" gutterBottom>{error}</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Tried: {Array.from(failedMethods).map(getMethodName).join(', ')}
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={handleRefresh}>Try Again</Button>
              <Button variant="contained" onClick={handleDownload} startIcon={<DownloadIcon />}>
                Download File
              </Button>
            </Stack>
          </Box>
        )}

        
        {!error && excelData && currentMethod === PREVIEW_METHODS.TABLE && (
          <Box sx={{ 
            flex: 1, 
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative"
          }}>
            {excelData.isEmpty ? (
              <Box sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 3
              }}>
                <Typography color="textSecondary">This spreadsheet is empty</Typography>
              </Box>
            ) : (
              <>
                
                <Box sx={{ 
                  flex: 1, 
                  overflowX: "auto",
                  overflowY: "hidden",
                  position: "relative",
                
                  '&::-webkit-scrollbar': {
                    height: '14px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f5f5f5',
                    borderRadius: '7px',
                    border: '1px solid #e0e0e0',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: '7px',
                    border: '2px solid #f5f5f5',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    '&:active': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  },
                  // Firefox scrollbar
                  scrollbarWidth: 'auto',
                  scrollbarColor: `${theme.palette.primary.main} #f5f5f5`,
                }}>
                  <DataGrid
                    rows={excelData.rows}
                    columns={excelData.columns}
                    pageSize={25}
                    rowsPerPageOptions={[25, 50, 100]}
                    disableSelectionOnClick
                    disableColumnResize={false}
                    sx={{ 
                      border: "none", 
                      height: "100%",
                      minWidth: "max-content",
                      
                      '& .MuiDataGrid-main': {
                        overflow: 'visible',
                      },
                      '& .MuiDataGrid-virtualScroller': {
                        overflow: 'visible',
                      },
                      '& .MuiDataGrid-virtualScrollerContent': {
                        overflow: 'visible',
                      },
                      // Style the table cells
                      '& .MuiDataGrid-cell': {
                        borderRight: '1px solid #e0e0e0',
                        padding: '8px 12px',
                      },
                      '& .MuiDataGrid-columnHeader': {
                        borderRight: '1px solid #e0e0e0',
                        backgroundColor: '#fafafa',
                        fontWeight: 600,
                      },
                      // Remove default borders
                      '& .MuiDataGrid-row': {
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                      },
                    }}
                    hideFooterSelectedRowCount
                    density="compact"
                    components={{
                      NoRowsOverlay: () => (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                          <Typography color="textSecondary">No data available</Typography>
                        </Box>
                      ),
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        )}

        {canPreview && currentMethod !== PREVIEW_METHODS.TABLE && !error && (
          <Box sx={{ flex: 1, position: "relative" }}>
            <iframe
              ref={iframeRef}
              title={`Preview of ${fileName}`}
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          </Box>
        )}

        
        {!canPreview && !excelData && !error && !isLoading && (
          <Box sx={{
            flex: 1,
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center"
          }}>
            <DocumentIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
            <Typography variant="h6" gutterBottom>Preview Not Available</Typography>
            <Typography color="textSecondary" sx={{ mb: 2 }}>
              This file type cannot be previewed. You can download it to view.
            </Typography>
            <Button variant="contained" onClick={handleDownload} startIcon={<DownloadIcon />}>
              Download File
            </Button>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default FilePreview;
